import type { WorkerInfo } from "@rivets/core";
import { usableWorkers } from "@/features/workers/worker-access";

export interface WorkerCluster {
  id: string;
  name: string;
  workers: WorkerInfo[];
}

/**
 * The org's workers grouped into the clusters routing actually addresses, by
 * name. Task creation and automations route the same way, so they read the
 * same list rather than each grouping the worker roster their own way.
 */
export function workerClusters(workers: readonly WorkerInfo[]): WorkerCluster[] {
  const clusters = new Map<string, WorkerCluster>();
  for (const worker of workers) {
    const cluster = clusters.get(worker.clusterId);
    if (cluster) cluster.workers.push(worker);
    else clusters.set(worker.clusterId, { id: worker.clusterId, name: worker.clusterName, workers: [worker] });
  }
  return [...clusters.values()].sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * The clusters a member may route to: those with at least one worker they
 * may use, each listing only those workers. A cluster of machines nobody
 * shared with them is not offered at all — pinning it would be refused
 * (`worker_cluster_access_denied`), and its capacity is not theirs to count.
 */
export function usableWorkerClusters(workers: readonly WorkerInfo[]): WorkerCluster[] {
  return workerClusters(usableWorkers(workers));
}
